/**
 * Signing in.
 *
 * There is no password. OneID is the only way in, by design (BLAC-123), and
 * the panel never sees a credential -- it receives a one-time ticket and
 * trades it for tokens.
 *
 *   browser  GET  /api/auth/oneid/start/?panel=cabinet
 *            ... OneID ...
 *            302 -> <cabinet origin>/#oneid=<ticket>
 *   panel    POST /api/auth/oneid/exchange/ {ticket}  -> {access, refresh}
 *
 * The ticket arrives in the URL *fragment* rather than the query string
 * because a fragment is not sent to the server and does not reach a proxy or
 * an access log. It is still visible in the address bar and in history, which
 * is why it is one-time, short-lived, and cleared from the URL the moment it
 * is read.
 */
import axios from "axios";
import api, { API_URL, NAV_API_URL, tokens } from "./api";

/** Reads and immediately erases the fragment OneID sent us back with. */
function takeFragment() {
  if (typeof window === "undefined" || !window.location.hash) return {};
  const params = new URLSearchParams(window.location.hash.slice(1));
  const ticket = params.get("oneid") || undefined;
  const error = params.get("oneid_error") || undefined;
  if (!ticket && !error) return {};
  // Replace rather than push: the ticket must not be reachable with Back.
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  return { ticket, error };
}

const ONEID_ERRORS = {
  oneid_disabled: "OneID tizimi profilaktika jarayonida. Keyinroq urinib koʻring.",
  oneid_unconfigured: "OneID sozlanmagan. Administratorga murojaat qiling.",
  oneid_unavailable: "OneID javob bermadi. Keyinroq urinib koʻring.",
  oneid_rejected: "OneID kirishni rad etdi. Qaytadan urinib koʻring.",
  oneid_cancelled: "Kirish bekor qilindi.",
  oneid_not_individual: "Iltimos, jismoniy shaxs nomidan kiring.",
  oneid_no_access: "Sizga bu tizimga kirish huquqi berilmagan. Administratorga murojaat qiling.",
  oneid_wrong_panel: "Bu kabinet mijoz tizimlari uchun. Ichki xodimlar boshqaruv panelidan foydalanadi.",
  oneid_state_invalid: "Kirish urinishi eskirgan. Qaytadan boshlang.",
  oneid_error: "Kirishda xatolik yuz berdi.",
};

export const messageFor = (code) => ONEID_ERRORS[code] || ONEID_ERRORS.oneid_error;

/** Survives the redirect that raised it, for the sign-in page to show. */
const PENDING_ERROR = "mysuhbat.signin_error";
export const takePendingError = () => {
  const v = sessionStorage.getItem(PENDING_ERROR);
  if (v) sessionStorage.removeItem(PENDING_ERROR);
  return v || "";
};

// `panel=cabinet` is what the server checks the account's role against: a
// staff login arriving here is refused at the callback, not after sign-in.
export const oneidStartUrl = () => `${NAV_API_URL}/auth/oneid/start/?panel=cabinet`;

/** What the sign-in screen may offer. Public; no token needed. */
export async function oneidStatus() {
  const res = await api.get("/auth/oneid/status/");
  return res.data;
}

/**
 * Runs before React renders.
 *
 * Doing this ahead of the router matters: the redirect lands on "/", which is
 * behind the route guard, and a guard that fires first would bounce to
 * /sign-in and take the fragment with it.
 */
export async function bootstrapSession() {
  const { ticket, error } = takeFragment();
  if (error) {
    sessionStorage.setItem(PENDING_ERROR, messageFor(error));
    return;
  }
  if (!ticket) return;
  try {
    // A bare axios call: the instance's interceptor would attach a stale
    // Authorization header and try to refresh a session that does not exist.
    const res = await axios.post(`${API_URL}/auth/oneid/exchange/`, { ticket });
    tokens.set(res.data.access, res.data.refresh);
  } catch {
    sessionStorage.setItem(PENDING_ERROR, "Bu kirish havolasining muddati tugagan. Qaytadan urinib koʻring.");
  }
}

export async function signOut() {
  const refresh = tokens.refresh;
  try {
    if (refresh) await api.post("/auth/logout/", { refresh });
  } catch {
    // A logout that cannot reach the server still has to end the session here.
  } finally {
    tokens.clear();
    window.location.href = "/sign-in";
  }
}
